import React, { useRef, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { getKeyFromItem } from '../../utils/shared';
import { Stack } from '../stack';
import { DropdownItem } from '../dropdown/DropdownItem';
import { buildCSSWithLength } from '../../styles';
import { Composite, CompositeItem } from '@floating-ui/react';
import { getTimePickerOption } from './utils';
import { text_style_specs } from '../../foundation/typography';

export interface TimeOption {
  label: string;
  value: number;
  index: number;
  disabled: boolean;
}

export interface TimePickerOptionsProps {
  availableHeight: number;
  fill?: boolean;
  timeHourPickerOptions: TimeOption[];
  timeMinutePickerOptions: TimeOption[];
  selectedTimeHour?: number;
  selectedTimeMinute?: number;
  onChangeHour?: (option: number) => void;
  onChangeMinute?: (option: number) => void;
}

export const TIME_PICKER_COLUMN_HEIGHT = 340;
export const TIME_PICKER_OPTION_VERTICAL_PADDING = 8;
export const TIME_PICKER_CELL_HEIGHT = text_style_specs.Body_13.lineHeight + TIME_PICKER_OPTION_VERTICAL_PADDING * 2;
export const TIME_PICKER_SAFE_AREA = 100;

export const TimePickerOptions = ({
  availableHeight,
  timeHourPickerOptions,
  timeMinutePickerOptions,
  selectedTimeHour,
  selectedTimeMinute,
  fill = false,
  onChangeHour,
  onChangeMinute,
}: TimePickerOptionsProps) => {
  const [hourActiveIndex, setHourActiveIndex] = useState<number>(
    selectedTimeHour ? getTimePickerOption(timeHourPickerOptions, selectedTimeHour)?.index ?? 0 : 0,
  );
  const [minuteActiveIndex, setMinuteActiveIndex] = useState<number>(
    selectedTimeMinute ? getTimePickerOption(timeMinutePickerOptions, selectedTimeMinute)?.index ?? 0 : 0,
  );
  const timeHourRef = useRef<HTMLUListElement | null>(null);
  const timeMinuteRef = useRef<HTMLUListElement | null>(null);

  const timePickerHeight = availableHeight - TIME_PICKER_SAFE_AREA;
  const columnHeight = timePickerHeight > TIME_PICKER_COLUMN_HEIGHT ? TIME_PICKER_COLUMN_HEIGHT : timePickerHeight;

  const scrollHour = (behavior: ScrollBehavior = 'smooth') => {
    if (selectedTimeHour) {
      const selectedHourOption = getTimePickerOption(timeHourPickerOptions, selectedTimeHour);
      updateScrollPosition('hour', selectedHourOption?.index ?? 0, behavior);
    }
  };

  const scrollMinute = (behavior: ScrollBehavior = 'smooth') => {
    if (selectedTimeMinute) {
      const selectedMinuteOption = getTimePickerOption(timeMinutePickerOptions, selectedTimeMinute);
      updateScrollPosition('minute', selectedMinuteOption?.index ?? 0, behavior);
    }
  };

  const updateScrollPosition = (target: 'hour' | 'minute', index: number, behavior: ScrollBehavior) => {
    const targetContainer = {
      hour: timeHourRef.current,
      minute: timeMinuteRef.current,
    };

    if (targetContainer[target]) {
      targetContainer[target]?.scrollTo({
        top: TIME_PICKER_CELL_HEIGHT * index,
        behavior,
      });
    }
  };

  useEffect(() => {
    scrollHour();
  }, [selectedTimeHour]);

  useEffect(() => {
    scrollMinute();
  }, [selectedTimeMinute]);

  useEffect(() => {
    scrollHour('auto');
    scrollMinute('auto');
  }, []);

  return (
    <Stack direction='row' gap={4} width={fill ? '100%' : 'auto'}>
      <Composite
        ref={timeHourRef}
        activeIndex={hourActiveIndex}
        onNavigate={setHourActiveIndex}
        render={<TimeList $fill={fill} columnHeight={columnHeight} cellHeight={TIME_PICKER_CELL_HEIGHT} />}
      >
        {timeHourPickerOptions.map(({ label, value, disabled }, index) => {
          return (
            <CompositeItem
              key={getKeyFromItem({ label, value }, index)}
              render={
                <TimeDropdownItem
                  disabled={disabled}
                  selected={selectedTimeHour === value}
                  size='medium'
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !disabled) {
                      event.preventDefault();
                      onChangeHour?.(value);
                    }
                    if (event.key === '') {
                      event.preventDefault();
                    }
                  }}
                  onClick={() => !disabled && onChangeHour?.(value)}
                >
                  {label}
                </TimeDropdownItem>
              }
            />
          );
        })}
      </Composite>
      <Composite
        ref={timeMinuteRef}
        activeIndex={minuteActiveIndex}
        onNavigate={setMinuteActiveIndex}
        render={<TimeList $fill={fill} columnHeight={columnHeight} cellHeight={TIME_PICKER_CELL_HEIGHT} />}
      >
        {timeMinutePickerOptions.map(({ label, value, disabled }, index) => {
          return (
            <CompositeItem
              key={getKeyFromItem({ label, value }, index)}
              render={
                <TimeDropdownItem
                  disabled={disabled}
                  selected={selectedTimeMinute === value}
                  size='medium'
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !disabled) {
                      event.preventDefault();
                      onChangeMinute?.(value);
                    }
                    if (event.key === '') {
                      event.preventDefault();
                    }
                  }}
                  onClick={() => !disabled && onChangeMinute?.(value)}
                >
                  {label}
                </TimeDropdownItem>
              }
            />
          );
        })}
      </Composite>
    </Stack>
  );
};

const TimeList = styled.ul<{ columnHeight: number; cellHeight: number; $fill: boolean }>`
  position: relative;
  margin: 0;
  padding: 0;
  list-style: none;
  overflow-y: auto;
  overflow-x: hidden;

  ${({ $fill }) =>
    $fill &&
    `
      width: 100%;
      li {
        width: 100%;
      }
    `}
  ${({ columnHeight, cellHeight }) =>
    `
      ${buildCSSWithLength('height', columnHeight)};
      &:after {
        ${buildCSSWithLength('height', columnHeight - cellHeight)};
        display: block;
        content: '';
      }
  `};
`;

const TimeDropdownItem = styled(DropdownItem)`
  && {
    height: auto;
    padding: ${TIME_PICKER_OPTION_VERTICAL_PADDING}px;
  }
  width: 64px;
  justify-content: center;
`;
